/**
 * @module  support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { CartNotEmptyModalService } from '@support/shared/services/cart-not-empty-modal.service';
import {
  CartNotEmptyModel,
  CartNotEmptyServiceEmitterInterface,
  CartNotEmptyRouteObject,
} from '@support/shared/widgets/interfaces/cart-not-empty-modal.interface';
import { APIError } from '@support/api/common/error.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { takeUntil } from 'rxjs/operators';
/**
 * Cart Not Empty Modal Component is a commom component for handling the mix cart scenarios
 *  with a static template body. This modal
 * can be triggered through user actions in the DOM or through provider actions.
 */
@Component({
  selector: 'tmo-cart-not-empty-modal',
  templateUrl: './cart-not-empty-modal.component.html',
})
export class CartNotEmptyModalComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  @ViewChild('mod', { static: true }) public selectModal: ElementRef;
  /**
   * configurable StaticModal input interface
   */
  public modal: CartNotEmptyModel;
  /**
   * route to redirect after clear cart
   */
  public navigateToRoute: CartNotEmptyRouteObject;
  /**
   * boolean to show spinner
   */
  public showLoader: boolean;
  /**
   * Boolean value to show/hide modal .
   */
  public displayModal: Boolean;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * API Error Handleing Object
   */
  public errorMessage: APIError;
  /**
   * boolean to check Retail Channel
   */
  // public isRetail: boolean;
  /**
   * constructor function for static modal.
   */
  constructor(
    private objectService: ObjectService,
    private route: Router,
    private cartSupportService: CartSupportService,
    private cartNotEmptyModalService: CartNotEmptyModalService,
    public activatedRoute: ActivatedRoute,
    public pdlService: PdlService
  ) {
    super(pdlService, activatedRoute);
    this.showLoader = false;
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * this method is executed on load.
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    // this.isRetail = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
    this.cartNotEmptyModalService.emitter
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: CartNotEmptyServiceEmitterInterface) => {
        switch (event.method) {
          case 'open':
            this.open(event.arg);
            break;
          case 'close':
            this.displayModal = false;
            break;
          default:
            break;
        }
      });
  }

  /**
   * To reset the modal and open
   * @param none
   * @returns void
   */
  private open(navigateToRoute: CartNotEmptyRouteObject): void {
    this.initializeModel();
    this.navigateToRoute = navigateToRoute;
    this.displayModal = true;
    // this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    this.focusModal();
  }
  /**
   * This method emits handleEvent which is used to execute
   * the functionality on particular button click.
   * @param event contains information on which button user has clicked
   */
  public handleClickEvent(event: { id: string }): void {
    const viewCartButtonId = this.objectService.get(this.modal.buttons[0], 'id');
    const continueButtonId = this.objectService.get(this.modal.buttons[1], 'id');
    if (event.id === viewCartButtonId) {
      // this.isRetail ? this.route.navigate(['plan-services']) :
      //     this.route.navigate(['/cart/review']);
      this.route.navigate(['/cart/review']);
    } else if (event.id === continueButtonId) {
      this.showLoader = true;
      this.cartSupportService
        .deleteCart()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          () => {
            this.showLoader = false;
            // if (this.isRetail) {
            //     this.stateStoreUtilsService.setUpgradeDevice(true);
            //     // this.stateStoreUtilsService.setLineAndDeviceTiles([]);
            //     this.stateStoreUtilsService.setRecordsAsValid(false);
            //     this.route.navigate(['plan-services']);
            //     return;
            // }
            if (this.navigateToRoute.queryParams) {
              this.route.navigate([this.navigateToRoute.navigateToRoute], {
                queryParams: this.navigateToRoute.queryParams,
              });
            } else {
              this.route.navigate([this.navigateToRoute.navigateToRoute]);
            }
          },
          (error: APIError[]) => {
            // TODO PO will create story for error handling
            this.errorMessage = error[0];
            this.showLoader = false;
          }
        );
    }
    this.displayModal = false;
  }

  /**
   * Destroys all the subscipritions.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Force the focus on the modal after render.
   */
  public focusModal(): void {
    // @ToDo: Remove Timeout when global stack execution pattern exist
    const timeInterval: number = 0;
    setTimeout(() => {
      this.selectModal.nativeElement.focus();
    }, timeInterval);
  }

  /**
   * Initialize the modal with default values.
   * @param none
   * @returns void
   */
  private initializeModel(): void {
    this.modal = {
      headerText: 'Oops! You have to clear your cart',
      bodyText:
        'Finish your previous transactions first or ' +
        'continue with this selection and your cart will be cleared.',
      disableDismiss: false,
      disableEsc: false,
      buttons: [
        {
          id: 'viewCart',
          name: 'View Cart',
          type: 'secondary',
          class: 'btn btn-secondary',
        },
        {
          id: 'continue',
          name: 'Continue',
          class: 'btn btn-primary',
          type: 'submit',
        },
      ],
      dismissableMask: true,
      modalName: 'cart-not-empty-modal',
    };
  }
}
